import { InputAdornment } from '@material-ui/core';
import { FilledInputProps } from '@material-ui/core/FilledInput';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import Check from '@material-ui/icons/Check';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { KeyCodeUtils } from '../../shared/util/keypress-utils';
import { ValidationResult } from '../../shared/util/validation-utils';
import './field-basic.scss';

type PartialTextFieldProps = Pick<
    TextFieldProps,
    'className' | 'margin' | 'autoFocus' | 'onFocus' | 'onBlur' | 'autoComplete' | 'type' | 'InputProps' | 'InputLabelProps' | 'disabled'
>;

export interface FieldBasicsProps extends PartialTextFieldProps {
    label: string;
    value?: string;
    readOnly?: boolean;
    inputRef?: React.RefObject<HTMLInputElement>;
    hideUnderline?: boolean;
    showValidation?: boolean;
    onChange?: (value: string) => void;
    validate?: (value: string) => ValidationResult;
    onEnter?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
    helpText?: string;
    externalUpdate?: boolean;
    hasExternalSanitization?: boolean;
}

export const FieldBasic = (props: FieldBasicsProps) => {
    const { value: propValue, onChange, validate, readOnly, showValidation, hideUnderline, onEnter, helpText, InputProps, externalUpdate, hasExternalSanitization=false, ...rest } = props;
    const { t } = useTranslation();

    const [value, setValue] = React.useState<string>(props.value ?? '');
    const [isFocused, setFocused] = React.useState<boolean>(false);

    const [validation, setValidation] = React.useState<ValidationResult>(
        validate && props.value ? validate(props.value) : { isValid: true }
    );

    React.useEffect(() => {
        if (externalUpdate) {
            setValue(ps => (ps === propValue ? ps : propValue ?? ''));
        }
    }, [externalUpdate, propValue]);

    React.useEffect(() => {
        if (showValidation && validate) {
            const _validation = validate(value);
            setValidation(_validation);
        }
    }, [validate, value, showValidation]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const _value = event.target.value;
        if (_value === value) {
            return;
        }
        if (onChange) {
            onChange(_value);
        }
        if(!hasExternalSanitization)
            setValue(_value);
    };

    const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!KeyCodeUtils.isEnterKey(event)) return;
        event.preventDefault();
        if (onEnter) {
            onEnter(event);
        }
    };

    const handleFocus = () => setFocused(true);
    const handleBlur = () => setFocused(false);

    const validAdornment = (
        <InputAdornment position="end">
            <Check />
        </InputAdornment>
    );

    const adornment: Pick<FilledInputProps, 'endAdornment'> | undefined =
        showValidation && validation.isValid ? { endAdornment: validAdornment } : undefined;

    const className = [
        'text__field',
        props.className,
        value && 'text__has-value',
        isFocused && 'text__field--focused',
        showValidation && validation.isValid && 'text__field--valid',
        showValidation && !validation.isValid && 'text__field--has-error',
        hideUnderline && `hide-underline`
    ]
        .filter(it => !!it)
        .join(' ');

    return (
        <TextField
            id="text__field"
            fullWidth
            {...rest}
            // caution with props order
            // https://github.com/mui-org/material-ui/issues/15697
            variant="filled"
            InputProps={{ readOnly: props.readOnly, ...adornment, ...InputProps }}
            onChange={handleChange}
            className={className}
            value={value}
            autoComplete={props.autoComplete}
            error={showValidation && !!validate && !validation.isValid}
            helperText={showValidation && !!validate && !validation.isValid ? t(validation.errorMessage) : isFocused ? helpText : ''}
            inputRef={props.inputRef}
            onKeyDown={handleKeyPress}
            onFocus={handleFocus}
            onBlur={handleBlur}
        />
    );
};

export default FieldBasic;
